import React, { useContext, useEffect, useRef, useState } from "react";
import axios, { CancelTokenSource } from "axios";
import { Alert, FormGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import FileUpload from "go-app/components/ImageForm/FileUpload";
import handleError from "go-app/services/errors";
import { ReactComponent as EmptyImageSVG } from "go-component/images/svg/angle-input.svg";
import handleException from "go-core/api/handleException";
import { ApiError } from "go-core/api/types";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import {
	CsvImportOverviewFormType,
	ExportCSVTranslationsApi,
	ImportCsvOverviewApi,
	ImportOverviewConfig,
} from "../../services/types";
import AttributesTable from "./components/AttributesTable";
import FileDisplay from "./components/FileDisplay";
import ImportConfirm from "./components/ImportConfirm";
import ImportOverview from "./components/ImportOverview/ImportOverview";
import PreviewItemsWithErrors from "./components/PreviewItemsWithErrors/PreviewItemsWithErrors";
import { CsvContext } from "./services/context";
import { AttributeProps, ImportFormProps } from "./utils/types";

interface Props {
	resourceType: string;
	customFieldsConfig?: CustomFieldTemplateApi[];
	redirectionUrl: string;
	importCsvRequestParams: string;
	comparationCheck?: (data: any, fieldName: string) => any;
	renderComparedValue?: (data: any, fieldName: string, differs?: boolean) => JSX.Element;
	translations: ExportCSVTranslationsApi;
	customAttributesCompare?: (attribute: string) => string;
	workingInBackground?: boolean;
	importOverviewConfig?: ImportOverviewConfig;
	omitExporting?: true;
}

const ImportCsv = ({
	resourceType,
	customFieldsConfig,
	redirectionUrl,
	importCsvRequestParams,
	comparationCheck,
	renderComparedValue,
	translations,
	customAttributesCompare,
	workingInBackground,
	importOverviewConfig,
	omitExporting,
}: Props): JSX.Element => {
	const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
	const [attributes, setAttributes] = useState<AttributeProps[]>([]);
	const [errors, setErrors] = useState<ApiError[]>([]);
	const [response, setResponse] = useState<any>(undefined);
	const [loading, setLoading] = useState(false);
	const [overviewData, setOverviewData] = useState<ImportCsvOverviewApi | undefined>(undefined);
	const [mappingsWithConfig, setMappingsWithConfig] = useState<CsvImportOverviewFormType | undefined>(undefined);
	const [stepNo, setStepNo] = useState(1);
	const { addFlash } = useFlash();
	const { t } = useTranslation("lib");
	const {
		export_csv: exportCSV,
		resource_name: resourceName,
		constraints,
		database_column_name: databaseColumnName,
	} = translations;
	const csvImportContext = useContext(CsvContext);
	const mainDivRef = useRef<HTMLDivElement>(null);
	const importTimeoutRef = useRef<number | undefined>();
	const cancelTokenSource = useRef<CancelTokenSource>();
	const applyNewWayOfImporting = Boolean(importOverviewConfig);

	useEffect(() => {
		return () => {
			if (cancelTokenSource.current !== undefined) {
				cancelTokenSource.current.cancel();
			}
			window.clearTimeout(importTimeoutRef.current);
		};
	}, []);

	const onUpdateFile = (file: string, fileObj?: File) => setSelectedFile(fileObj);

	const getSimpleExampleFile = async () => {
		try {
			const res = await csvImportContext.getCsvExampleSimpleFile(resourceType);
			const url = window.URL.createObjectURL(new Blob([res.data]));
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", `${resourceType}_simple.csv`);
			document.body.appendChild(link);
			link.click();
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleImportTimeout = (
		jobUid: string,
		formData: FormData,
		_headers: Record<string, string>,
		params: Record<string, any>,
		customStepNumberOnSuccess?: number
	) => {
		if (!csvImportContext.getImportCsvProgress) return;

		importTimeoutRef.current = window.setTimeout(async () => {
			try {
				if (!csvImportContext.getImportCsvProgress) return;
				const CancelToken = axios.CancelToken;
				const newCancelToken = CancelToken.source();
				cancelTokenSource.current = newCancelToken;

				const res = await csvImportContext.getImportCsvProgress(jobUid, resourceType, params, {
					cancelToken: newCancelToken.token,
				});
				if (res.status === 204) {
					setLoading(true);
					handleImportTimeout(jobUid, formData, _headers, params, customStepNumberOnSuccess);
				} else {
					setLoading(false);
					setResponse(res.data.data);
					setStepNo(customStepNumberOnSuccess ?? 3);
					window.clearTimeout(importTimeoutRef.current);
				}
			} catch (err) {
				const errs = handleException(err);
				if (errs[0].message === "canceled") return;
				handleError.alert(err, addFlash);
				setErrors(errs);
				setStepNo(customStepNumberOnSuccess ?? 3);
				setLoading(false);
				window.clearTimeout(importTimeoutRef.current);
			}
		}, 1000);
	};

	const handleImport = async (dataForm: ImportFormProps) => {
		setAttributes(dataForm.attributes);
		setLoading(true);
		const params: Record<string, string> = { include: importCsvRequestParams };
		try {
			const formData = new FormData();
			if (selectedFile) formData.append("csv", selectedFile);

			formData.append(
				"attributes",
				new Blob(
					[
						JSON.stringify(
							dataForm.attributes.filter(
								(f: { domain_field_name: string }) => f.domain_field_name?.length > 0
							)
						),
					],
					{ type: "application/json" }
				)
			);
			const _headers = { "Content-Type": "multipart/form-data" };

			if (
				workingInBackground &&
				(csvImportContext.importAsyncCsvWithMappings || csvImportContext.importAsyncCsv)
			) {
				const CancelToken = axios.CancelToken;
				const newCancelToken = CancelToken.source();
				cancelTokenSource.current = newCancelToken;
				if (applyNewWayOfImporting) {
					const overviewDataResponse = await csvImportContext.importAsyncCsvWithMappings?.(
						resourceType,
						formData,
						_headers,
						{
							cancelToken: newCancelToken.token,
						}
					);

					setOverviewData(overviewDataResponse);
					setLoading(false);
					setStepNo(3);
				} else {
					const response = await csvImportContext.importAsyncCsv?.(resourceType, formData, _headers, params, {
						cancelToken: newCancelToken.token,
					});
					handleImportTimeout(response, formData, _headers, params);
				}
			} else if (csvImportContext.importCsv) {
				const res = await csvImportContext.importCsv(resourceType, formData, _headers, params);
				setResponse(res);
			}
		} catch (err) {
			const errs = handleException(err);
			if (errs[0].message === "canceled") return;
			handleError.alert(err, addFlash);
			setErrors(errs);
		}
		if (!workingInBackground) {
			setStepNo(3);
			setLoading(false);
		}
	};

	const getExampleFile = async () => {
		try {
			const res = await csvImportContext.getCsvExampleFile(resourceType);
			const url = window.URL.createObjectURL(new Blob([res.data]));
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", `${resourceType}.csv`); //or any other extension
			document.body.appendChild(link);
			link.click();
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const exportCsv = async () => {
		try {
			const res = await csvImportContext.getCsvFromVenue(resourceType);
			const url = window.URL.createObjectURL(new Blob([res.data]));
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", `${resourceType}.csv`);
			document.body.appendChild(link);
			link.click();
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const processImport = async () => {
		if (selectedFile) {
			setLoading(true);
			try {
				const formData = new FormData();
				formData.append("csv", selectedFile);
				const headers = { "Content-Type": "multipart/form-data" };
				const res = await csvImportContext.getCsvAttributes(resourceType, formData, headers);
				setAttributes(res);
				setStepNo(2);
			} catch (e) {
				handleError.alert(e, addFlash);
			}
		}
		setLoading(false);
	};

	const getStepDescription = () => {
		if (!applyNewWayOfImporting) {
			return (
				<>
					{`"${t("go_component.import_csv.action.import.title")} ${resourceName}:`}
					{stepNo === 1 && ` ${t("go_component.import_csv.action.choose_file.title")}`}
					{stepNo === 2 && ` ${t("go_component.import_csv.action.match_column.title")}`}
					{stepNo === 3 && ` ${t("go_component.import_csv.field.operation_confirm.title")}`}"
				</>
			);
		}

		return (
			<span className="d-block">
				{(stepNo === 1 || stepNo === 2) &&
					`"${t("go_component.import_csv.action.import.title")} ${resourceName}:`}
				{stepNo === 1 && ` ${t("go_component.import_csv.action.choose_file.title")}"`}
				{stepNo === 2 && ` ${t("go_component.import_csv.action.match_column.title")}"`}
				{stepNo === 3 && ""}
				{stepNo === 4 &&
					response?.length > 0 &&
					`"${t("go_component.import_csv.field.resource_preview.title", {
						resourceName,
					})}"`}
			</span>
		);
	};

	return (
		<div className={"import-page"} ref={mainDivRef}>
			<div className={"mb-10"}>
				<strong className={"import-info"}>
					{`${t("go_component.import_csv.field.step.title")} ${stepNo} ${t("common.word.of", {
						ns: "lib",
					})} ${applyNewWayOfImporting ? 4 : 3} `}
				</strong>
				{getStepDescription()}
			</div>
			{stepNo === 1 && (
				<>
					<FileUpload
						fileIcon={
							selectedFile ? (
								<FileDisplay icon={<EmptyImageSVG />} fileName={selectedFile.name} />
							) : undefined
						}
						onUpdateFile={onUpdateFile}
					/>
					<div className="mb-2">
						<span className="action-ref d-block" onClick={getSimpleExampleFile}>
							{t("go_component.import_csv.action.download_simple_csv_example.title")}
						</span>
						<span className="action-ref d-block" onClick={getExampleFile}>
							{t("go_component.import_csv.action.download_csv_example.title")}
						</span>
						{!omitExporting && (
							<span className="action-ref d-block" onClick={exportCsv}>
								{exportCSV}
							</span>
						)}
					</div>
					<FormGroup>
						{selectedFile && (
							<ButtonLoading onClick={processImport} loading={loading}>
								{t("common.action.goto")}
							</ButtonLoading>
						)}
					</FormGroup>
					<Alert variant="warning">
						<div className="d-flex flex-column">
							<strong className="import-info">{`${t(
								"go_component.import_csv.field.info.title"
							)}:`}</strong>
							<span>{t("go_component.import_csv.field.file_info.title")}</span>
						</div>
					</Alert>
				</>
			)}
			{stepNo === 2 && (
				<div className="d-flex flex-column">
					<AttributesTable
						loading={loading}
						customFieldsConfig={customFieldsConfig}
						attributes={attributes}
						handleImport={(data) => handleImport(data)}
						redirectionUrl={redirectionUrl}
						databaseColumnName={databaseColumnName}
					/>
				</div>
			)}
			{stepNo === 3 && !applyNewWayOfImporting && comparationCheck && renderComparedValue && (
				<div className="d-flex flex-column">
					<ImportConfirm
						parentResponseData={response}
						parentErrors={errors}
						selectedFile={selectedFile}
						attributes={attributes}
						resourceType={resourceType}
						customFieldsConfig={customFieldsConfig}
						importCsvRequestParams={importCsvRequestParams}
						redirectionUrl={redirectionUrl}
						comparationCheck={comparationCheck}
						renderComparedValue={renderComparedValue}
						databaseColumnName={databaseColumnName}
						constraints={constraints}
						customAttributesCompare={customAttributesCompare}
						workingInBackground={workingInBackground}
						importPageRef={mainDivRef}
					/>
				</div>
			)}
			{stepNo === 3 && importOverviewConfig && overviewData && (
				<ImportOverview
					loading={loading}
					setLoading={setLoading}
					translations={translations}
					importOverviewConfig={importOverviewConfig}
					resourceType={resourceType}
					overviewData={overviewData}
					selectedFile={selectedFile}
					attributes={attributes}
					handleImportTimeout={handleImportTimeout}
					setMappingsWithConfig={setMappingsWithConfig}
				/>
			)}
			{stepNo === 4 && importOverviewConfig && (
				<PreviewItemsWithErrors
					errors={errors}
					response={response}
					selectedFile={selectedFile}
					attributes={attributes}
					importOverviewConfig={importOverviewConfig}
					translations={translations}
					workingInBackground
					resourceType={resourceType}
					redirectionUrl={redirectionUrl}
					mappingsWithConfig={mappingsWithConfig}
				/>
			)}
		</div>
	);
};
export default ImportCsv;

// t("common.action.import_from_csv")
